import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const TrialContext = createContext();

export const useTrial = () => {
  const context = useContext(TrialContext);
  if (!context) {
    throw new Error('useTrial must be used within TrialProvider');
  }
  return context;
};

export const TrialProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const notifiedRef = useRef({ warning24h: false, expired: false });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTrialStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTrialStatus(data);
      }
    } catch (error) {
      logger.error('Failed to fetch trial status:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTrialStatus();
      // Poll every minute to update trial countdown
      const interval = setInterval(fetchTrialStatus, 60000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchTrialStatus]);

  // Check notification preferences
  const getTrialNotificationsEnabled = () => {
    const saved = sessionStorage.getItem('trialNotificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  };

  // Trial milestone notification logic
  useEffect(() => {
    if (!trialStatus || !user) return;
    if (!getTrialNotificationsEnabled()) return;

    const userId = user.user_id || user.id || '';
    const notifKey = `trial_notifs_${userId}`;
    const shown = JSON.parse(sessionStorage.getItem(notifKey) || '{}');

    const hours = trialStatus.hours_remaining || 0;
    const isActive = trialStatus.is_active || false;

    // Skip for permanent access users (hours_remaining > 9000 means admin/subscriber)
    if (hours > 9000) return;

    // 24h warning: trial is active AND hours between 0 and 24
    if (isActive && hours > 0 && hours <= 24 && !shown.warning24h && !notifiedRef.current.warning24h) {
      notifiedRef.current.warning24h = true;
      sessionStorage.setItem(notifKey, JSON.stringify({ ...shown, warning24h: true }));
      window.dispatchEvent(new CustomEvent('trialNotification', {
        detail: {
          type: 'trial_warning',
          message: `Your free trial expires in ${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m — subscribe to keep full access!`,
        }
      }));
    }

    // Expired: trial is no longer active AND we haven't notified
    if (!isActive && hours <= 0 && trialStatus.trial_end && !shown.expired && !notifiedRef.current.expired) {
      // Only notify if user actually had a trial (trial_end exists and is in the past)
      const trialEnd = new Date(trialStatus.trial_end);
      const now = new Date();
      if (trialEnd < now) {
        notifiedRef.current.expired = true;
        sessionStorage.setItem(notifKey, JSON.stringify({ ...shown, expired: true }));
        window.dispatchEvent(new CustomEvent('trialNotification', {
          detail: {
            type: 'trial_expired',
            message: 'Your free trial has ended. Subscribe to unlock all premium features!',
          }
        }));
      }
    }
  }, [trialStatus, user]);

  const checkVariantUnlocked = async (variantId) => {
    if (!isAuthenticated) return false;

    // Standard and Fight to be King always unlocked
    if (variantId === 'standard' || variantId === 'fight_to_be_king') {
      return true;
    }

    try {
      const response = await fetch(`${API_URL}/api/trial/variant/${variantId}/unlocked`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.unlocked;
      }
    } catch (error) {
      logger.error('Failed to check variant access:', error);
    }

    return false;
  };

  const formatTimeRemaining = (hours) => {
    if (!hours || hours <= 0) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const hasActiveAccess = () => {
    // User has access if trial is active OR if they're authenticated (purchased)
    return trialStatus?.is_active || false;
  };

  const value = {
    trialStatus,
    loading,
    isTrialActive: trialStatus?.is_active || false,
    hoursRemaining: trialStatus?.hours_remaining || 0,
    timeRemainingFormatted: trialStatus ? formatTimeRemaining(trialStatus.hours_remaining) : '',
    trialTimeRemaining: trialStatus ? formatTimeRemaining(trialStatus.hours_remaining) : '',
    checkVariantUnlocked,
    refreshTrialStatus: fetchTrialStatus,
    hasActiveAccess
  };

  return (
    <TrialContext.Provider value={value}>
      {children}
    </TrialContext.Provider>
  );
};
